import { memo } from 'react';
import { Handle, Node, NodeProps, Position } from '@xyflow/react';
import {
  Plus,
  Trash2,
} from 'lucide-react';
import styled from 'styled-components';
import { SkillsNodeData } from '../types';
import { backendPalette } from '../../../components/backend/BackendCardSystem';
import PortalAvatar from '../../../components/profile/PortalAvatar';
import { useAuth } from '../../../context/AuthProvider';

const NodeShell = styled.div<{ $dragging?: boolean }>`
  position: relative;
  width: 230px;

  &:hover .node-actions,
  &:focus-within .node-actions {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }

  ${({ $dragging }) =>
    $dragging
      ? `
  .node-actions {
    opacity: 0 !important;
    pointer-events: none !important;
  }
  `
      : ''}
`;

const Card = styled.article<{
  $selected?: boolean;
  $ancestor?: boolean;
  $dropTarget?: boolean;
  $dragging?: boolean;
}>`
  position: relative;
  width: 230px;
  min-height: 132px;
  border-radius: 14px;
  border: ${({ $dragging }) =>
    $dragging
      ? '1.5px dashed rgba(14, 14, 14, 0.55)'
      : 'none'};
  background: linear-gradient(
    155deg,
    ${({ $dragging }) =>
      $dragging
        ? 'rgba(224, 161, 94, 0.52) 0%, rgba(224, 161, 94, 0.45) 60%, rgba(219, 154, 86, 0.48) 100%'
        : 'rgba(224, 161, 94, 0.98) 0%, rgba(224, 161, 94, 0.95) 60%, rgba(219, 154, 86, 0.96) 100%'}
  );
  box-shadow: ${({ $dropTarget, $selected, $ancestor }) =>
    $dropTarget
      ? `0 16px 30px rgba(0, 0, 0, 0.34), 0 0 0 2px ${backendPalette.green}`
      : $selected
      ? '0 16px 32px rgba(0, 0, 0, 0.34), 0 0 0 2px rgba(14, 14, 14, 0.78)'
      : $ancestor
      ? '0 12px 24px rgba(0, 0, 0, 0.28), 0 0 0 1px rgba(14, 14, 14, 0.42)'
      : '0 10px 22px rgba(0, 0, 0, 0.24)'};
  color: rgba(14, 14, 14, 0.95);
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 8px;
  padding: 10px;
  transition: box-shadow 0.15s ease, transform 0.15s ease, opacity 0.15s ease;
  transform: ${({ $dragging }) =>
    $dragging ? 'translateY(-1px) rotate(-1deg) scale(1.015)' : 'none'};
  opacity: ${({ $dragging }) => ($dragging ? 0.74 : 1)};
  cursor: ${({ $dragging }) => ($dragging ? 'grabbing' : 'grab')};

  ${({ $dragging }) =>
    $dragging
      ? `
  &::after {
    content: '';
    position: absolute;
    inset: 4px -8px -8px 8px;
    border-radius: 12px;
    border: 1px dashed rgba(14, 14, 14, 0.35);
    background: rgba(224, 161, 94, 0.18);
    z-index: -1;
    transform: rotate(-1.5deg);
  }
  `
      : ''}
`;

const Title = styled.div`
  font-size: 0.92rem;
  font-weight: 900;
  line-height: 1.2;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DetailList = styled.div`
  display: grid;
  gap: 3px;
`;

const DetailValue = styled.span`
  font-size: 0.76rem;
  color: rgba(18, 18, 18, 0.9);
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AgentFooter = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-self: flex-start;
  min-width: 0;
`;

const AgentAvatar = styled(PortalAvatar)`
  flex: 0 0 auto;
`;

const AgentName = styled.span`
  font-size: 0.76rem;
  color: rgba(14, 14, 14, 0.94);
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionRail = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translate(-50%, -4px);
  z-index: 8;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px;
  border-radius: 999px;
  // border: 1px solid rgba(255, 255, 255, 0.2);
  // background: rgba(8, 12, 28, 0.92);
  // box-shadow: 0 10px 18px rgba(0, 0, 0, 0.35);
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms ease, transform 150ms ease;
`;

const HoverRadius = styled.div`
  position: absolute;
  top: 100%;
  left: -24px;
  right: -24px;
  height: 64px;
  z-index: 7;
`;

const ActionButton = styled.button<{ $tone: 'neutral' | 'danger' }>`
  width: 30px;
  height: 30px;
  border: ${({ $tone }) =>
    $tone === 'neutral'
      ? `1px dashed ${backendPalette.grey}`
      : 'none'};
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === 'danger' ? backendPalette.red : 'transparent'};
  color: ${({ $tone }) =>
    $tone === 'neutral' ? backendPalette.green : backendPalette.black};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

function normalizeMetaValue(value: unknown): string {
  if (typeof value !== 'string') return '--';
  const trimmed = value.trim();
  return trimmed.length ? trimmed : '--';
}

function getNodeDetail(node: SkillsNodeData['node'], key: string) {
  if (!node.meta || typeof node.meta !== 'object') return '--';
  return normalizeMetaValue((node.meta as Record<string, unknown>)[key]);
}

function getNodeMetaText(node: SkillsNodeData['node'], key: string): string | null {
  if (!node.meta || typeof node.meta !== 'object') return null;
  const value = (node.meta as Record<string, unknown>)[key];
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

type SkillFlowNode = Node<SkillsNodeData, 'skillNode'>;

function NodeCardImpl({ data }: NodeProps<SkillFlowNode>) {
  const { user: authUser } = useAuth();
  const {
    node,
    isSelected,
    isAncestor,
    isDropTarget,
    isDragging,
    canAddChild,
    canDelete,
    onAddChild,
    onDelete,
  } = data;
  const showActions = canAddChild || canDelete;
  const designationValue = getNodeDetail(node, 'designation');
  const positionValue = getNodeDetail(node, 'position');
  const appointedNameValue = getNodeDetail(node, 'appointedAgent');
  const appointedName = appointedNameValue !== '--' ? appointedNameValue : '--';
  const appointedUidValue = getNodeDetail(node, 'appointedAgentUid');
  const appointedEmailValue = getNodeDetail(node, 'appointedAgentEmail');
  const appointedUidRaw = appointedUidValue !== '--' ? appointedUidValue : '';
  const appointedEmailRaw = appointedEmailValue !== '--' ? appointedEmailValue : '';
  const appointedEmailLower = appointedEmailRaw.trim().toLowerCase();
  const authEmailLower = (authUser?.email ?? '').trim().toLowerCase();
  const isAppointedUserSelf =
    (appointedUidRaw && appointedUidRaw === (authUser?.uid ?? '')) ||
    (appointedEmailLower && appointedEmailLower === authEmailLower);

  // Keep avatar fallback parity with Users/CRM accordion headers so one user identity
  // renders consistently in both places (Brain nodes and CRM rows).
  const avatarUrl =
    getNodeMetaText(node, 'appointedAgentAvatarUrl') ??
    getNodeMetaText(node, 'appointedAgentPhotoURL') ??
    getNodeMetaText(node, 'appointedAgentPhotoUrl') ??
    getNodeMetaText(node, 'appointedAgentAvatarURL') ??
    getNodeMetaText(node, 'appointedAgentAvatar') ??
    getNodeMetaText(node, 'appointedAgentProfileImageUrl') ??
    getNodeMetaText(node, 'appointedAgentImageUrl') ??
    getNodeMetaText(node, 'appointedAgentImage') ??
    (isAppointedUserSelf ? authUser?.photoURL?.trim() ?? null : null);

  return (
    <NodeShell $dragging={isDragging}>
      <Card
        $selected={isSelected}
        $ancestor={isAncestor}
        $dropTarget={isDropTarget}
        $dragging={isDragging}
      >
        <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
        <DetailList>
          <Title>{designationValue !== '--' ? designationValue : node.title}</Title>
          <DetailValue>{positionValue}</DetailValue>
        </DetailList>
        <AgentFooter>
          <AgentAvatar
            uid={appointedUidValue !== '--' ? appointedUidValue : undefined}
            email={appointedEmailValue !== '--' ? appointedEmailValue : undefined}
            name={appointedName === '--' ? '--' : appointedName}
            photoURL={avatarUrl ?? undefined}
            size={26}
            implicitSelfFallback={false}
            disableNavigation={appointedUidValue === '--' && appointedEmailValue === '--'}
          />
          <AgentName>{appointedName}</AgentName>
        </AgentFooter>
        <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      </Card>
      <HoverRadius className="node-hover-radius" />
      {showActions ? (
        <ActionRail className="node-actions">
          {canAddChild ? (
            <ActionButton
              type="button"
              $tone="neutral"
              aria-label="Branch"
              title="Branch"
              onClick={(event) => {
                event.stopPropagation();
                onAddChild?.(node.id);
              }}
            >
              <Plus size={12} />
            </ActionButton>
          ) : null}
          {canDelete ? (
            <ActionButton
              type="button"
              $tone="danger"
              aria-label="Delete"
              title="Delete"
              onClick={(event) => {
                event.stopPropagation();
                onDelete?.(node.id);
              }}
            >
              <Trash2 size={12} />
            </ActionButton>
          ) : null}
        </ActionRail>
      ) : null}
    </NodeShell>
  );
}

export const NodeCard = memo(NodeCardImpl);
